import { generatePath, matchPath } from 'react-router';
import type { Action } from 'redux';
import { all, call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { FETCH_DEALER_FAILURE, FETCH_DEALER_SUCCESS, SELECT_DEALER } from 'constants/ActionTypes';
import { NEW_ENTITY_ID } from 'constants/EntityTitles';
import { SELF_SERVICE } from 'constants/ExtensionConstants';
import { PATH } from 'constants/RoutesConstants';
import { putAndWait } from 'modules/app/sagas';
import { scopeToPage } from 'modules/app/sagas/utils';
import { simpleRequestsLoader } from 'modules/app/utils';
import * as actions from 'modules/cameras/actions';
import * as types from 'modules/cameras/actionTypes';
import { DASHBOARD_SOURCE, DETAILS_SOURCE } from 'modules/cameras/constants';
import requestsConfig from 'modules/cameras/requestsConfig';
import { getBaseRequestParams } from 'modules/cameras/selectors';
import type { BasicDealer, CreateCameraSuccessAction, DetailedCamera } from 'modules/cameras/types';
import { selectDealer } from 'modules/dealers/actions';
import { fetchGatesFilter } from 'modules/gates/actions/core';
import { CAMERA_GATE_SELECT_ID, GATE_SELECT, GATE_STATUS_ENUM, OPENING_METHODS } from 'modules/gates/constants';
import { isApiFailure } from 'modules/global/generic.guards';
import type { PutAndWaitResult } from 'modules/global/generic.types';
import type { MayBe } from 'modules/global/typing/common/common';
import { navigate } from 'modules/router/navigate';
import { getSelectedDealer } from 'selectors/dealerSelectors';
import { getCurrentLocation } from 'selectors/router';

function* refreshDashboard() {
  const { page: _, ...params }: ReturnType<typeof getBaseRequestParams> & { page?: unknown } =
    yield select(getBaseRequestParams);
  yield put(actions.fetchCameras({ ...params, page: 0 }, { source: DASHBOARD_SOURCE }));
}

const FILTER_ACTION_TYPES: Array<string> = [
  SELECT_DEALER,
  types.SET_CAMERAS_DASHBOARD_FILTER,
  types.SET_CAMERAS_DASHBOARD_FILTERS,
  types.CLEAR_CAMERAS_DASHBOARD_FILTER,
  types.REMOVE_CAMERAS_DASHBOARD_FILTER,
];

const onFilterChange = ({ type }: Action) => FILTER_ACTION_TYPES.includes(type);

function* onPageWorker() {
  yield call(refreshDashboard);
  yield takeLatest(onFilterChange, refreshDashboard);
}

function* afterUpdateWorker({ payload, error }: CreateCameraSuccessAction) {
  if (!error) {
    yield call(navigate, generatePath(PATH.CAMERAS_DETAILS.path, { cameraId: payload.id }), { replace: true });
  }
}

function* onDetailsPageWorker() {
  const pathname: ReturnType<typeof getCurrentLocation> = yield select(getCurrentLocation);
  const { cameraId } = matchPath(PATH.CAMERAS_DETAILS.path, pathname)?.params ?? {};
  if (cameraId && cameraId !== NEW_ENTITY_ID) {
    // getSelectedDealer is still JS (any) — typed to the fields this worker reads.
    const selectedDealer: MayBe<{ id: string; extensions?: Array<string> }> =
      yield select(getSelectedDealer);
    const response: PutAndWaitResult<DetailedCamera> = yield putAndWait(
      actions.fetchCamera(cameraId, { source: DETAILS_SOURCE })
    );
    if (!isApiFailure(response)) {
      const camera = response.payload;
      const defaultDealerId =
        camera.dealers.find((dealer: BasicDealer) => dealer.id === selectedDealer?.id)?.id ?? camera.dealers[0]?.id;
      if (defaultDealerId && selectedDealer?.id !== defaultDealerId) {
        yield put(selectDealer(defaultDealerId));
        yield take([FETCH_DEALER_SUCCESS, FETCH_DEALER_FAILURE]);
      }
      const dealerIds = camera.dealers.map((dealer: BasicDealer) => dealer.id);
      const hasSelfService = selectedDealer?.extensions?.includes(SELF_SERVICE);
      if (hasSelfService && dealerIds.length > 0) {
        yield put(
          fetchGatesFilter(
            { dealerIds, types: [OPENING_METHODS.RELAY], status: GATE_STATUS_ENUM.ACTIVE },
            { source: GATE_SELECT, selectId: CAMERA_GATE_SELECT_ID },
          ),
        );
      }
    }
  }
  yield put(actions.setIsReady());
  yield takeEvery([types.POST_CAMERA_SUCCESS, types.POST_CAMERA_FAILURE], afterUpdateWorker);
}

export default function* CameraManagementSagas() {
  yield scopeToPage([PATH.CAMERAS], onPageWorker);
  yield scopeToPage([PATH.CAMERAS_DETAILS], onDetailsPageWorker);

  yield all(simpleRequestsLoader(requestsConfig));
}
